import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { challengesAPI } from '../utils/api';
import FlagEditor from './admin/FlagEditor';
import { Drawer } from './shared/DrawerManager';
import './Dashboard.css';
import './CandidateDashboard.css';

function ChallengeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [labStatus, setLabStatus] = useState('stopped'); // stopped, starting, running
  const [labUrl, setLabUrl] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [flagInput, setFlagInput] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagResult, setFlagResult] = useState(null);
  const [registeredIP, setRegisteredIP] = useState('');

  useEffect(() => {
    console.log('[ChallengeDetail] Component mounted, labStatus:', labStatus);
    loadChallenge();
    checkActiveSession();
  }, [id]);

  useEffect(() => {
    console.log('[ChallengeDetail] labStatus changed to:', labStatus);
  }, [labStatus]);

  // Countdown timer effect
  useEffect(() => {
    if (labStatus !== 'running' || !expiresAt) {
      setTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeRemaining(0);
        setLabStatus('stopped');
        setLabUrl('');
        setModalMessage('Your lab session has expired.');
        setShowModal(true);
        setTimeout(() => setShowModal(false), 3000);
        return;
      }

      setTimeRemaining(diff);
    };

    updateTimer(); // Initial update
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [labStatus, expiresAt]);

  const loadChallenge = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await challengesAPI.getById(id);
      setChallenge(response.data.challenge);
    } catch (error) {
      console.error('[ChallengeDetail] Failed to load challenge:', error);
      setError('Failed to load challenge details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkActiveSession = async () => {
    try {
      console.log('[ChallengeDetail] Checking for active session...');

      const response = await fetch(`/api/sessions/challenge/${id}/active`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': localStorage.getItem('sessionId')
        }
      });

      const data = await response.json();

      if (response.ok && data.session) {
        console.log('[ChallengeDetail] Found active session:', data.session.sessionId);

        // Restore session state
        setLabUrl(data.session.sessionUrl);
        setSessionId(data.session.sessionId);
        setExpiresAt(data.session.expiresAt);
        setRegisteredIP(data.session.registeredIP || '');
        setLabStatus('running');
      } else {
        console.log('[ChallengeDetail] No active session found');
      }
    } catch (error) {
      console.error('[ChallengeDetail] Failed to check active session:', error);
      // Don't show error to user - just silently fail to 'stopped' state
    }
  };

  const handleStartChallenge = async () => {
    try {
      setLabStatus('starting');
      setError('');
      setModalMessage('Setting up your secure lab environment...\n\nIMPORTANT: This lab will be locked to your current IP address. You will not be able to access it from a different network or location.');
      setShowModal(true);

      // Create challenge session
      const response = await fetch(`/api/sessions/challenge/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': localStorage.getItem('sessionId')
        }
      });

      const data = await response.json();

      if (response.ok && data.session) {
        setLabUrl(data.session.sessionUrl);
        setSessionId(data.session.sessionId);
        setExpiresAt(data.session.expiresAt);
        setRegisteredIP(data.session.registeredIP || '');
        setLabStatus('running');
        setShowModal(false);

        // Open challenge in new tab
        window.open(data.session.sessionUrl, '_blank');
      } else {
        throw new Error(data.error || 'Failed to create session');
      }

    } catch (error) {
      console.error('[ChallengeDetail] Failed to start lab:', error);
      setError(error.message || 'Failed to start lab environment. Please try again.');
      setLabStatus('stopped');
      setModalMessage('Failed to start lab environment.\n\nPlease try again.');
      setTimeout(() => {
        setShowModal(false);
      }, 3000);
    }
  };

  const handleStopLab = async () => {
    try {
      setModalMessage('Stopping lab environment...');
      setShowModal(true);

      // TODO: Stop session via API
      setTimeout(() => {
        setLabStatus('stopped');
        setLabUrl('');
        setModalMessage('Lab environment stopped successfully.');
        setTimeout(() => {
          setShowModal(false);
        }, 2000);
      }, 1000);
    } catch (error) {
      console.error('[ChallengeDetail] Failed to stop lab:', error);
      setError('Failed to stop lab environment.');
      setShowModal(false);
    }
  };

  const handleGoBack = () => {
    navigate('/candidate');
  };

  const handleSubmitFlag = async (e) => {
    e.preventDefault();

    if (!flagInput.trim()) {
      setFlagResult({ success: false, message: 'Please enter a flag' });
      return;
    }

    try {
      setFlagSubmitting(true);
      setFlagResult(null);

      const response = await fetch(`/api/sessions/${sessionId}/flags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': localStorage.getItem('sessionId')
        },
        body: JSON.stringify({ flag: flagInput.trim() })
      });

      const data = await response.json();

      if (response.ok) {
        setFlagResult({ success: true, message: data.message || 'Flag submitted successfully!' });
        if (data.correct) {
          setFlagInput('');
        }
      } else {
        throw new Error(data.error || 'Failed to submit flag');
      }
    } catch (error) {
      console.error('[ChallengeDetail] Failed to submit flag:', error);
      setFlagResult({ success: false, message: error.message || 'Failed to submit flag' });
    } finally {
      setFlagSubmitting(false);
    }
  };

  const formatTimeRemaining = (milliseconds) => {
    if (!milliseconds || milliseconds <= 0) return '00:00:00';

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const getTimerClass = (milliseconds) => {
    if (!milliseconds) return '';
    const totalMinutes = Math.floor(milliseconds / 60000);
    if (totalMinutes <= 5) return 'timer-critical';
    if (totalMinutes <= 15) return 'timer-warning';
    return 'timer-normal';
  };

  const getDifficultyBadgeClass = (difficulty) => {
    const baseClass = 'difficulty-badge';
    switch (difficulty?.toLowerCase()) {
      case 'easy':
      case 'junior': return `${baseClass} difficulty-easy`;
      case 'medium':
      case 'senior': return `${baseClass} difficulty-medium`;
      case 'hard':
      case 'advanced': return `${baseClass} difficulty-hard`;
      case 'expert':
      case 'redteam': return `${baseClass} difficulty-expert`;
      default: return baseClass;
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Loading challenge details...</p>
        </div>
      </div>
    );
  }

  if (error || !challenge) {
    return (
      <div className="dashboard">
        <div className="error-state">
          <h2>Error</h2>
          <p>{error || 'Challenge not found'}</p>
          <button onClick={handleGoBack} className="btn btn-secondary">
            Back to Challenges
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard candidate-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>Challenge Details</h1>
          <div className="user-info">
            <span>{user?.name || user?.email}</span>
            <button onClick={logout} className="btn btn-secondary">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="challenge-detail-container">
          <div className="challenge-detail-header">
            <button onClick={handleGoBack} className="btn btn-link">
              ← Back to Challenges
            </button>
          </div>

          <div className="challenge-detail-card">
            <div className="card-header">
              <h2>{challenge.title}</h2>
              <span className={getDifficultyBadgeClass(challenge.difficulty)}>
                {challenge.difficulty?.toUpperCase() || 'MEDIUM'}
              </span>
            </div>

            <div className="card-body">
              <div className="challenge-section">
                <h3>Description</h3>
                <p>{challenge.description}</p>
              </div>

              {challenge.scenario && (
                <div className="challenge-section">
                  <h3>Scenario</h3>
                  <p>{challenge.scenario}</p>
                </div>
              )}

              <div className="challenge-metadata">
                <div className="metadata-item">
                  <label>Category:</label>
                  <span className="category-badge">{challenge.category}</span>
                </div>
                <div className="metadata-item">
                  <label>Points:</label>
                  <span className="points-badge">{challenge.totalPoints || 100} points</span>
                </div>
                {challenge.timeLimit && (
                  <div className="metadata-item">
                    <label>Time Limit:</label>
                    <span>{challenge.timeLimit} minutes</span>
                  </div>
                )}
                {challenge.deliveryMethod && (
                  <div className="metadata-item">
                    <label>Environment:</label>
                    <span className="env-badge">{challenge.deliveryMethod.toUpperCase()}</span>
                  </div>
                )}
              </div>

              {/* Flag Editor - Only visible to admin/recruiter */}
              <FlagEditor
                challenge={challenge}
                userRole={user?.role}
                onUpdate={(updatedFlags) => {
                  setChallenge({ ...challenge, flags: updatedFlags });
                }}
              />

              {challenge.skills && challenge.skills.length > 0 && (
                <div className="challenge-section">
                  <h3>Skills Tested</h3>
                  <div className="skills-tags">
                    {challenge.skills.map((skill, idx) => (
                      <span key={idx} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
              )}

              {challenge.objectives && challenge.objectives.length > 0 && (
                <div className="challenge-section">
                  <h3>Objectives</h3>
                  <ul className="objectives-list">
                    {challenge.objectives.map((objective, idx) => (
                      <li key={idx}>{objective}</li>
                    ))}
                  </ul>
                </div>
              )}

              {challenge.hints && challenge.hints.length > 0 && (
                <div className="challenge-section">
                  <h3>Hints Available</h3>
                  <p>This challenge has {challenge.hints.length} hint(s) available if you get stuck.</p>
                </div>
              )}
            </div>

            {/* Lab URL Display */}
            {labStatus === 'running' && labUrl && (
              <div className="lab-active-section">
                <h3>Lab Environment Active</h3>
                {timeRemaining !== null && (
                  <div className={`timer-display ${getTimerClass(timeRemaining)}`}>
                    <div className="timer-content">
                      <div className="timer-label">Time Remaining</div>
                      <div className="timer-value">{formatTimeRemaining(timeRemaining)}</div>
                    </div>
                  </div>
                )}
                {registeredIP && (
                  <div className="ip-notice">
                    <div className="ip-content">
                      <div className="ip-label">Registered IP Address</div>
                      <div className="ip-value">{registeredIP}</div>
                      <div className="ip-warning">This lab can only be accessed from this IP address</div>
                    </div>
                  </div>
                )}
                <p>Your challenge environment is ready. Click below to access it:</p>
                <div className="lab-url-container">
                  <a
                    href={labUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="lab-url-link"
                  >
                    {labUrl}
                  </a>
                </div>

                {/* Flag Submission */}
                <div className="flag-submission-section">
                  <h4>Submit Flag</h4>
                  <p>Found the flag? Submit it below:</p>
                  <form onSubmit={handleSubmitFlag} className="flag-form">
                    <div className="flag-input-group">
                      <input
                        type="text"
                        value={flagInput}
                        onChange={(e) => setFlagInput(e.target.value)}
                        placeholder="Enter flag (e.g., FLAG{...})"
                        className="flag-input"
                        disabled={flagSubmitting}
                      />
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={flagSubmitting || !flagInput.trim()}
                      >
                        {flagSubmitting ? 'Submitting...' : 'Submit Flag'}
                      </button>
                    </div>
                    {flagResult && (
                      <div className={`flag-result ${flagResult.success ? 'flag-success' : 'flag-error'}`}>
                        {flagResult.message}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            )}

            <div className="card-actions">
              {labStatus === 'stopped' && (
                <button
                  className="btn btn-primary btn-large"
                  onClick={handleStartChallenge}
                >
                  Start Lab
                </button>
              )}
              {labStatus === 'starting' && (
                <button
                  className="btn btn-primary btn-large"
                  disabled
                >
                  Starting Lab...
                </button>
              )}
              {labStatus === 'running' && (
                <>
                  <button
                    className="btn btn-success btn-large"
                    onClick={() => window.open(labUrl, '_blank')}
                  >
                    Open Lab
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleStopLab}
                  >
                    Stop Lab
                  </button>
                </>
              )}
              <button
                className="btn btn-secondary"
                onClick={handleGoBack}
              >
                Back to Challenges
              </button>
            </div>
          </div>

          <div className="challenge-instructions">
            <h3>Before You Start</h3>
            <ul>
              <li>Make sure you understand the challenge scenario</li>
              <li>An isolated environment will be created for hands-on testing</li>
              <li>The lab will be locked to your current IP address</li>
              <li>You cannot share or access the lab from a different network or location</li>
              <li>You can request hints but they may affect your score</li>
              <li>Submit flags in the exact format specified</li>
              <li>Document your methodology for evaluation</li>
            </ul>
          </div>
        </div>
      </main>

      {/* Modal for lab status */}
      <Drawer
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Loading"
        width="sm"
      >
        <div className="drawer-loading">
          <div className="drawer-spinner"></div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '1rem', color: '#6B7280' }}>
          {modalMessage.split('\n').map((line, idx) => (
            <p key={idx} style={{ margin: '0.5rem 0' }}>{line}</p>
          ))}
        </div>
      </Drawer>
    </div>
  );
}

export default ChallengeDetail;
